import { PATechnician } from "./technician";
import { PATechnicianDate } from "./technician-date";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import { Subscription } from "rxjs";
import { PATour } from "./tour";
import { PAUtil } from "./util";
import { DraggableDestinationMarker } from "./draggable-marker";
import { MapContainer } from "../map/pa-map";
import { PATimeControl } from "../singletons/pa-time-control";
import { PALocation } from "./location";
import { PADataControl } from "../singletons/pa-data-control";
import { PASettingsControl } from "../singletons/pa-settings-control";

export class TechnicianDateCollection extends FireUpdateOnChange<{route_changed_for_technician_date_with_timestamp: number} | 'selected_tour' | 'changed_technician_dates' | 'changed_tours'> {

  private _includeTechnicianDatesForDayTimestamps: number[] = []
  private _hideTechnicianDatesForDayTimestamps: number[] = []

  private _activeSubscriptionMap = new Map<number, {new_tour_location_changes: Subscription, new_tour_changes: Subscription}>()
  private _showTourMode: 'base' | 'new' = 'new'

  private _selectedTourDayTimestamp: number
  public technicianDates: PATechnicianDate[] = []
  public tours: PATour[] = []

  private _activateTravelTechnicianDatesForTimestamps: number[] = []
  public destinationMarkerMap = new Map<number, DraggableDestinationMarker>()
  public shownDestinationMarker: DraggableDestinationMarker[] = []

  private _autoOptimizeTravelDestinationsForDayTimestamps: number[] = []

  private _mapContainer: MapContainer

  constructor(
    public technician: PATechnician
  ) {
    super()
  }

  executeBeforeChange(): void {}

  get hiddenTechnicianDates(): PATechnicianDate[] {
    return this.technicianDates.filter(td => !this._hideTechnicianDatesForDayTimestamps.includes(td.day.utc_timestamp))
  }

  get showTourMode(): 'base' | 'new' {
    return this._showTourMode
  }

  set showTourMode(show_tour_mode: 'base' | 'new') {
    this._showTourMode = show_tour_mode
  }

  get autoOptimizeTravelDestinationsForDayTimestamps(): number[] {
    return this._autoOptimizeTravelDestinationsForDayTimestamps
  }

  get mapContainer(): MapContainer {
    return this._mapContainer
  }

  set mapContainer(map_container: MapContainer) {
    this._mapContainer = map_container
    this.updateDestinationMarker()
  }

  get selectedTour(): PATour {
    if (this._selectedTourDayTimestamp) {
      const technician_date = this.technician.getTechnicianDate(this._selectedTourDayTimestamp, true, true)
      return this._showTourMode == 'new' ? technician_date.changedTourContainer.tour : technician_date.tour
    } else {
      return null
    }
  }

  public activateTravelTechnicianDateTimestamp(ts: number, skip_update?: boolean): void {
    if (!this._activateTravelTechnicianDatesForTimestamps.includes(ts)) {
      this._activateTravelTechnicianDatesForTimestamps.push(ts)
      if (!skip_update) this.updateDestinationMarker()
    }
  }

  public deactivateTravelTechnicianDateTimestamp(ts: number): void {
    if (this._activateTravelTechnicianDatesForTimestamps.includes(ts)) {
      this._activateTravelTechnicianDatesForTimestamps = this._activateTravelTechnicianDatesForTimestamps.filter(active_ts => ts != active_ts)
      this.updateDestinationMarker()
    }
  }

  public async addTechnicianDatesForDayTimestamps(day_tss: number[]): Promise<void> {
    for (const day_ts of day_tss.sort()) {
      if (!this._includeTechnicianDatesForDayTimestamps.includes(day_ts)) {
        this._includeTechnicianDatesForDayTimestamps.push(day_ts)

        const technician_date = this.technician.getTechnicianDate(day_ts, true, true)
        const week_day = technician_date.day.week_day
        const previous_technician_date = technician_date.getPreviousTechnicianDate(true)
        const day_ts_one_day_ago = day_ts - 24 * 60 * 60 * 1000

        if (
          PASettingsControl.Instance.selectedSettingConfig.data.travel_technicians.optimize_travel_locations.active &&
          PASettingsControl.Instance.selectedSettingConfig.data.travel_technicians.optimize_travel_locations.on_weekdays.includes(week_day)
        ) {
          this._autoOptimizeTravelDestinationsForDayTimestamps.push(day_ts)
        }

        await Promise.all([
          await technician_date.setTravelTechnicianDate(),
          await previous_technician_date.setTravelTechnicianDate()
        ])

        if (technician_date.previousTravelTechnicianDate?.isActive() && !this._includeTechnicianDatesForDayTimestamps.includes(day_ts_one_day_ago)) this.activateTravelTechnicianDateTimestamp(day_ts_one_day_ago, true)
        if (technician_date.travelTechnicianDate?.isActive()) this.activateTravelTechnicianDateTimestamp(day_ts, true)

        const new_location_subscription = this._getNewTourSubscriptionForTimestamp(day_ts);
        const new_tour_changed_subscription = technician_date.subscribeToChanges(
          _ => {
            this._activeSubscriptionMap.get(day_ts).new_tour_location_changes.unsubscribe()
            this._activeSubscriptionMap.get(day_ts).new_tour_location_changes = this._getNewTourSubscriptionForTimestamp(day_ts);
            this._updateTours()
          }
        )
        this._activeSubscriptionMap.set(day_ts, {new_tour_location_changes: new_location_subscription, new_tour_changes: new_tour_changed_subscription})
      }
    }

    this._includeTechnicianDatesForDayTimestamps.sort()
    this.updateDestinationMarker()
    this._updateTechnicianDates()
    this._updateTours()
  }

  private _getNewTourSubscriptionForTimestamp(day_ts: number): Subscription {
    const technician_date = this.technician.getTechnicianDate(day_ts, true, true)
    return technician_date.changedTourContainer.tour.subscribeToChanges(
      _ => {
        this.fireUpdate({route_changed_for_technician_date_with_timestamp: day_ts})
      }
    )
  }

  public removeTechnicianDateForDayTimestamp(day_ts: number): void {
    if (this._includeTechnicianDatesForDayTimestamps.includes(day_ts)) {
      this._includeTechnicianDatesForDayTimestamps.splice(this._includeTechnicianDatesForDayTimestamps.indexOf(day_ts), 1)
      this.removeSubscriptionsForDayTimestamp(day_ts);

      if (this._selectedTourDayTimestamp == day_ts) {
        this._selectedTourDayTimestamp = null
        this.fireUpdate('selected_tour')
      }

      this._updateTechnicianDates()
      this._updateTours()
    }
  }

  private removeSubscriptionsForDayTimestamp(day_ts: number) {
    const subscription = this._activeSubscriptionMap.get(day_ts)
    if (subscription) {
      subscription.new_tour_location_changes.unsubscribe()
      subscription.new_tour_changes.unsubscribe()
      this._activeSubscriptionMap.delete(day_ts)
    }
  }

  public hideTechnicianDateForDayTimestamp(day_ts: number): void {
    if (!this._hideTechnicianDatesForDayTimestamps.includes(day_ts)) {
      this._hideTechnicianDatesForDayTimestamps.push(day_ts)
    }
  }

  public showTechnicianDateForDayTimestamp(day_ts: number): void {
    if (this._hideTechnicianDatesForDayTimestamps.includes(day_ts)) {
      this._hideTechnicianDatesForDayTimestamps.splice(this._hideTechnicianDatesForDayTimestamps.indexOf(day_ts), 1)
    }
  }

  public clear(): void {
    for (const timestamp of [...this._includeTechnicianDatesForDayTimestamps]) {
      this.removeTechnicianDateForDayTimestamp(timestamp)
    }
  }

  public selectTourWithDayTimestamp(ts: number): void{
    if (this._includeTechnicianDatesForDayTimestamps.includes(ts)) {
      this._selectedTourDayTimestamp = ts
      this.fireUpdate('selected_tour')
    }
  }

  public unselectTour(): void {
    this._selectedTourDayTimestamp = null
    this.fireUpdate('selected_tour')
  }

  private _updateTechnicianDates(): void {
    const technician_dates_before = this.technicianDates
    this.technicianDates = this._includeTechnicianDatesForDayTimestamps.map(ts => this.technician.getTechnicianDate(ts, true, true)).filter(td => td)
    if (!PAUtil.equalSets(new Set(technician_dates_before), new Set(this.technicianDates))) {
      this.fireUpdate('changed_technician_dates')
    }
  }

  private _updateTours(): void {
    const tours_before = this.tours
    this.tours = this._includeTechnicianDatesForDayTimestamps.map(ts => this.technician.getTechnicianDate(ts, true, true)).filter(td => td).map(td => (this._showTourMode == 'base' || td.isPastTechnicianDate) ? td.tour : td.changedTourContainer.tour)
    if (!PAUtil.equalSets(new Set(tours_before), new Set(this.tours))) {
      this.fireUpdate('changed_tours')
      if (tours_before.find(tour => tour.technicianDate.day.utc_timestamp == this._selectedTourDayTimestamp)) {
        if (this.tours.find(tour => tour.technicianDate.day.utc_timestamp == this._selectedTourDayTimestamp)) {
          this.fireUpdate('selected_tour')
        } else {
          this.unselectTour()
        }
      }
    }
  }

  get technicianDateWithLivePosition(): PATechnicianDate {
    return this.technicianDates.find(td => td.isTodaysTechnicianDate)
  }

  isEmpty() {
    return !this._includeTechnicianDatesForDayTimestamps.length
  }

  getBaseTours(): PATour[] {
    return this.technicianDates.map(td => td.tour)
  }

  hasTechnicianDateWithTimestamp(ts: number): boolean {
    return this._includeTechnicianDatesForDayTimestamps.includes(ts);
  }

  updateDestinationMarker(): void {
    if (this._mapContainer?.map) {

      const show_marker_for_timestamps = [
        ...new Set(this._includeTechnicianDatesForDayTimestamps.concat(this._includeTechnicianDatesForDayTimestamps.map(ts => ts - 24 * 60 * 60 * 1000)))
      ].filter(ts => this._activateTravelTechnicianDatesForTimestamps.includes(ts))

      for (const day_ts of show_marker_for_timestamps) {
        let possible_marker = this.destinationMarkerMap.get(day_ts)
        if (!possible_marker) {
          const week_day = PATimeControl.Instance.getTimeStampsWeekDay(day_ts)
          const color = PAUtil.getIndexColorString(PATimeControl.Instance.weekDays.indexOf(week_day))
          const technician_date = this.technician.getTechnicianDate(day_ts, true)
          const next_technician_date = technician_date.getSubsequentTechnicianDate(true)
          this.destinationMarkerMap.set(day_ts, possible_marker = new DraggableDestinationMarker(
            this._mapContainer.map,
            technician_date,
            color,
            technician_date.getEndLocation().coordinates
          ))
          possible_marker.subscribeToChanges(_ => {
            this.onMarkerChange(possible_marker, technician_date, next_technician_date);
          })
        }
      }

      const shown_marker_before = this.shownDestinationMarker
      const shown_marker_after = show_marker_for_timestamps.map(ts => this.destinationMarkerMap.get(ts))
      shown_marker_after.map(marker => marker.map = this._mapContainer.map)

      const remove_marker = shown_marker_before.filter(marker_before => !shown_marker_after.find(marker_after => marker_after == marker_before))
      remove_marker.map(marker => {
        marker.map = null
        const timestamp = keyValue<number, DraggableDestinationMarker>(this.destinationMarkerMap, marker)
        const technician_date = this.technician.getTechnicianDate(timestamp, true)
        const next_technician_date = technician_date.getSubsequentTechnicianDate(true)
        technician_date.updateNewTourConfig({
          custom_end_location: 'remove'
        })
        next_technician_date.updateNewTourConfig({
          custom_start_location: 'remove'
        })
      })

      this.shownDestinationMarker = shown_marker_after
    } else {
      this.shownDestinationMarker.map(marker => marker.map = null)
    }
  }

  private onMarkerChange(possible_marker: DraggableDestinationMarker, technician_date: PATechnicianDate, next_technician_date: PATechnicianDate) {
    if (this.shownDestinationMarker.includes(possible_marker)) {
      const new_location = new PALocation(
        possible_marker.coordinates,
        PADataControl.Instance.distanceCalculator,
        PADataControl.Instance.generateNextLocationId(),
        null,
        null,
        null
      )
      technician_date.updateNewTourConfig(
        {
          custom_end_location: {
            location: new_location,
            marker: possible_marker
          }
        }
      )
      next_technician_date.updateNewTourConfig(
        {
          custom_start_location: {
            location: new_location,
            marker: possible_marker
          }
        }
      )
    }
  }

  getMarkerForTimestampIfActive(ts: number): DraggableDestinationMarker | null {
    const marker = this.destinationMarkerMap.get(ts)
    if (marker && this.shownDestinationMarker.includes(marker)) return marker
  }

  toggleAutomaticTravelDestinationOptimizationForTimestamp(timestamp: number, update_toggled_tour?: boolean) {
    if (this._autoOptimizeTravelDestinationsForDayTimestamps.includes(timestamp)) {
      this._autoOptimizeTravelDestinationsForDayTimestamps = this._autoOptimizeTravelDestinationsForDayTimestamps.filter(ts => ts != timestamp)
    } else {
      this._autoOptimizeTravelDestinationsForDayTimestamps.push(timestamp)
      if (update_toggled_tour) {
        const technician_date = this.technicianDates.find(td => td.day.utc_timestamp == timestamp)
        if (technician_date) {
          technician_date.updateNewTourConfig()
        }
      }
    }
  }
}

function keyValue<K, V>(map: Map<K, V>, searchValue: V): K {
  for (const [key, value] of map.entries()) {
    if (value === searchValue)
      return key;
  }
  return undefined;
}
